import { Globe, Shield, User, LogOut, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCallback, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ConfirmDialog from './ConfirmDialog'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { logout } = useAuth()
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const handleSignOut = useCallback(() => {
    setShowSignOutConfirm(true)
  }, [])

  const confirmSignOut = useCallback(async () => {
    try {
      setIsSigningOut(true)
      await logout()
    } finally {
      setIsSigningOut(false)
      setShowSignOutConfirm(false)
    }
  }, [logout])

  return (
    <footer className="bg-white border-t border-gray-200 mt-auto">
      <div className="w-full px-4 py-2 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-2">
          {/* Copyright & Profile */}
          <div className="flex items-center justify-center md:justify-start space-x-4 text-xs text-gray-500">
            <span>© {currentYear} AIOps24. All rights reserved.</span>
            <Link to="/profile" className="flex items-center space-x-1 hover:text-gray-900 transition-colors">
              <User className="w-3 h-3" />
              <span>Profile</span>
            </Link>
          </div>

          {/* Footer Search */}
          <div className="flex items-center justify-center">
            <div className="relative w-full max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-3.5 w-3.5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white focus:border-blue-500 text-xs transition-all"
                placeholder="Search POs, Requisitions, Vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Links + Version */}
          <div className="flex items-center justify-center md:justify-end gap-4 text-xs">
            <Link
              to="/privacy-policy" 
              className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Shield className="w-3 h-3" />
              <span>Privacy Policy</span>
            </Link>
            
            <Link 
              to="/terms-of-service" 
              className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Globe className="w-3 h-3" />
              <span>Terms of Service</span>
            </Link>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center justify-center p-1.5 text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-600 rounded-md transition-colors ring-1 ring-red-200/70"
              title="Sign Out"
              aria-label="Sign Out"
              type="button"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <span className="text-[10px] text-gray-400">Version 1.0.0</span>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={showSignOutConfirm}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={isSigningOut}
        onConfirm={confirmSignOut}
        onClose={() => {
          if (!isSigningOut) setShowSignOutConfirm(false)
        }}
      />
    </footer>
  )
}
